import React, { Ref, useState } from 'react'
import DataGrid, {
  Column,
  Button,
  Sorting,
  FilterRow,
  HeaderFilter,
  Scrolling,
  Paging,
  Pager,
  ColumnChooser,
  Export,
  Selection,
  LoadPanel,
  IColumnProps,
  ISelectionProps
} from 'devextreme-react/data-grid'
import { exportDataGrid } from 'devextreme/excel_exporter'
import './ICSDataGrid.css'
import { Workbook } from 'exceljs'
import saveAs from 'file-saver'
import DropDownButton from 'devextreme-react/drop-down-button'
import dxDataGrid from 'devextreme/ui/data_grid'
import { Row, Col } from 'reactstrap'
import DataSource, { DataSourceOptions } from 'devextreme/data/data_source'

interface ICSDataGridProps<T extends Array<any> = any[]> {
  dataSource: T
  columnOptions: IColumnProps[]
  options?: {
    noSort?: string[]
    noFilter?: string[]
    buttons?: JSX.Element[]
    dataGridRef?: Ref<DataGrid>
    selectAll?: any //per Dev Extream ISelectionProps 
    hideFilter?: boolean
    hideButtonRow?: boolean
    showScrollbar?: string
    virtualScroll?: boolean
  }
}

// Todo - SEV - Need to add support for a custom datasource that will support pagination and requrests from the grid to api
// https://js.devexpress.com/React/Documentation/ApiReference/Data_Layer/CustomStore/
const ICSDataGrid: React.FC<ICSDataGridProps> = <T extends Array<any>>({
    dataSource,
    columnOptions,
    options
  }: ICSDataGridProps<T>) => {
    const [selectedPageSize, setSelectedPageSize] = useState(10)
    const [exportedFiletype, setExportedFiletype] = useState('csv') //default is csv
    const availableFiletypes = ['.xlsx', '.csv']

    const onExporting = (e: {
      component?: dxDataGrid | undefined
      cancel?: boolean | undefined
    }) => {
      const workbook = new Workbook()
      const worksheet = workbook.addWorksheet('Main sheet')
      exportDataGrid({
        component: e.component,
        worksheet: worksheet
      }).then(function () {
        workbook[exportedFiletype]
          .writeBuffer()
          .then(function (buffer: BlobPart) {
            saveAs(
              new Blob([buffer], { type: 'application/octet-stream' }),
              `Datagrid.${exportedFiletype}`
            )
          })
      })
      e.cancel = true
    }

    const noSort =  options?.noSort || []
    const noFilter = options?.noFilter || []

    return (
      <div id="dataGridParent" >
        {!(options?.hideButtonRow || false) && <Row className="btnRow">
          {!options || !options.buttons
            ? null
            : options.buttons.map((item, key: number) => (
                <Col
                  className="p-0"
                  key={`btn_${key}`}
                  size={'auto'}
                >
                  {item}
                </Col>
              ))}
          <Col className="btnCol text-right" size={'auto'}>
            <DropDownButton
              splitButton={true}
              useSelectMode={false}
              text="Export"
              disabled={dataSource && dataSource.length === 0 ? true : false}
              items={availableFiletypes}
              onButtonClick={() => {
                const exportBtn = document.getElementsByClassName(
                  'dx-datagrid-export-button'
                )[0] as HTMLElement
                exportBtn.click()
              }}
              onItemClick={(e) => {
                setExportedFiletype(e.itemData.substring(1, e.itemData.length)) //we dont want the period in the front
              }}
            />
          </Col>
        </Row>}
        
        <DataGrid
          id="gridContainer"
          dataSource={dataSource}
          showRowLines={true}
          showBorders={true}
          rowAlternationEnabled={true}
          columnAutoWidth={true}
          allowColumnResizing={true}
          onExporting={onExporting}
          sorting={{mode:'multiple'}}
          headerFilter={{visible: !(options?.hideFilter || false)}}
          filterRow={{visible: !(options?.hideFilter || false)}}
          ref={options?.dataGridRef || null}
        >
          
          <LoadPanel enabled />
          {/* <Sorting mode="multiple" /> */}
          <Export enabled={!(options?.hideButtonRow || false)} />
          <FilterRow visible={true} />
          {/* <HeaderFilter visible={true} /> */}
          <Scrolling
            rowRenderingMode={options?.virtualScroll ? 'virtual' : 'standard'}
            showScrollbar={options?.showScrollbar || 'onHover'}
          />
          <Paging
            defaultPageSize={100}
            onPageSizeChange={(value) => setSelectedPageSize(value)}
          />
          {options?.selectAll && (
            <Selection
              mode="multiple"
              selectAllMode={options.selectAll?.allMode}
              showCheckBoxesMode={options.selectAll?.checkBoxesMode}
            />
          )}

          {!(options?.hideButtonRow || false) && <ColumnChooser mode="select" enabled={true} height="auto" />}
          {columnOptions.map((column, key: number) => {
            return (
              <Column
                key={`col_${key}`}
                defaultSortOrder={
                  column.defaultSortOrder
                    ? column.defaultSortOrder
                    : column.dataField === 'created_at' ||
                      column.dataField === 'updated_at'
                    ? 'desc'
                    : undefined
                }
                alignment={column.alignment}
                type={column.type}
                dataField={column.dataField}
                caption={column.caption}
                width={column.width}
                cellRender={column.cellRender}
                renderAsync={column.renderAsync}
                allowSorting={
                  noSort === undefined || !noSort.includes(column.caption)
                }
                allowFiltering={
                  !(options?.hideFilter || false) ? noFilter === undefined || !noFilter.includes(column.caption) : false
                }
                grouped={column.grouped}
                groupIndex={column.groupIndex}
                groupCellTemplate={column.groupCellTemplate}
              >
                {column.buttons &&
                  column.buttons.map((button, key: number) => {
                    const isVisible =
                      typeof button.visible === 'function' ||
                      typeof button.visible === 'boolean'
                        ? button.visible
                        : true
                    return (
                      <Button
                        key={`bu_${key}`}
                        visible={isVisible}
                        hint={button.hint}
                        icon={button.icon}
                        cssClass={button.cssClass}
                        onClick={button.onClick}
                        render={button.render}
                      />
                    )
                  })}
              </Column>
            )
          })}
        </DataGrid>
      </div>
    )
  }

export default ICSDataGrid
